'use client';

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { WELCOME_VIDEO_URL } from '@/lib/constants/welcome';

interface WelcomeVideoModalProps {
  userCreatedAt?: number; // Unix timestamp in seconds
}

export function WelcomeVideoModal({ userCreatedAt }: WelcomeVideoModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!userCreatedAt) return;

    // Check if user was created in the last 5 minutes
    const fiveMinutesMs = 5 * 60 * 1000;
    const createdAtMs = userCreatedAt * 1000;
    const isNewUser = Date.now() - createdAtMs < fiveMinutesMs;

    // Use sessionStorage to not show again on refresh within same session
    const hasSeenThisSession = sessionStorage.getItem('welcomeModalShown');

    if (isNewUser && !hasSeenThisSession) {
      setIsOpen(true);
    }
  }, [userCreatedAt]);

  const handleClose = () => {
    sessionStorage.setItem('welcomeModalShown', 'true');
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Welcome to Kreayt AI!</DialogTitle>
          <DialogDescription>
            Watch this quick video to learn how to create amazing AI-generated videos for your YouTube channel.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-md"
            src={`${WELCOME_VIDEO_URL}?autoplay=1`}
            title="Welcome to Kreayt AI"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <DialogFooter>
          <Button onClick={handleClose} className="w-full sm:w-auto">
            Get Started
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
